import React, { useState } from 'react';
import DialogueLesson, { Character, DialogueNode } from './DialogueLesson';
import AlgebraicProof from './AlgebraicProof';
import ExponentialBuilder from './ExponentialBuilder';

// ============ CHARACTERS ============

const characters: Character[] = [
  {
    id: 'alex',
    name: 'Alex',
    avatar: '🧑‍💻',
    color: '#8b5cf6',
    position: 'left',
  },
  {
    id: 'dr-euler',
    name: 'Dr. Euler',
    avatar: '👩‍🔬',
    color: '#ec4899',
    position: 'right',
  },
];

// ============ INTERACTIVE WRAPPER ============
// Wrapper for interactive algebraic proof

interface InteractiveProofProps {
  correctShapes: string[];
  targetLabel: string;
}

const InteractiveProof: React.FC<InteractiveProofProps> = ({ correctShapes, targetLabel }) => {
  const [selectedShapes, setSelectedShapes] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleCheck = () => {
    const correct = correctShapes.every(s => selectedShapes.includes(s)) &&
                   selectedShapes.every(s => correctShapes.includes(s));
    setIsCorrect(correct);
    setIsSubmitted(true);
  };

  const handleReset = () => {
    setSelectedShapes([]);
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-sm text-center" style={{ color: '#94a3b8' }}>
        Click on the shapes that represent <span className="font-bold text-purple-400">{targetLabel}</span>
      </p>

      <AlgebraicProof
        layout="four-rectangles"
        targetArea={targetLabel}
        showLabels={true}
        interactive={!isSubmitted}
        selectedShapes={selectedShapes}
        correctShapes={correctShapes}
        onSelectionChange={setSelectedShapes}
        isSubmitted={isSubmitted}
        isCorrect={isCorrect}
        size={260}
      />

      <div className="flex gap-2">
        {!isSubmitted ? (
          <button
            onClick={handleCheck}
            disabled={selectedShapes.length === 0}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: selectedShapes.length > 0 ? '#8b5cf6' : '#475569',
              color: '#ffffff',
              opacity: selectedShapes.length > 0 ? 1 : 0.5,
            }}
          >
            Check Selection
          </button>
        ) : (
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#475569', color: '#ffffff' }}
          >
            Try Again
          </button>
        )}
      </div>

      {isSubmitted && (
        <div
          className="px-4 py-2 rounded-lg text-sm"
          style={{
            background: isCorrect ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            color: isCorrect ? '#10b981' : '#ef4444',
          }}
        >
          {isCorrect ? 'Correct! Well done!' : 'Not quite - try selecting different shapes'}
        </div>
      )}
    </div>
  );
};

// ============ EXPONENTIAL CHALLENGE ============

const ExponentialChallenge: React.FC = () => {
  const [selectedA, setSelectedA] = useState<number | null>(null);
  const [selectedB, setSelectedB] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleCheck = () => {
    // y = 8 * (1/2)^n, but we're showing as y = 8 * 2^(-n)
    // So the equation is y = 8 × 2^(-n)
    const correct = selectedA === 8 && selectedB === 2;
    setIsCorrect(correct);
    setIsSubmitted(true);
  };

  const handleReset = () => {
    setSelectedA(null);
    setSelectedB(null);
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  // Points for exponential decay: y = 8 * (1/2)^n
  const points = [
    { x: 0, y: 8, label: '(0, 8)' },
    { x: 1, y: 4, label: '(1, 4)' },
    { x: 2, y: 2, label: '(2, 2)' },
  ];

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-sm text-center" style={{ color: '#94a3b8' }}>
        Build the exponential function that passes through these points
      </p>

      <ExponentialBuilder
        points={points}
        xAxisLabel="Days"
        yAxisLabel="Leaves"
        xMax={3}
        yMax={12}
        availableNumbers={[2, 4, 6, 8]}
        selectedA={selectedA}
        selectedB={selectedB}
        onSelectA={setSelectedA}
        onSelectB={setSelectedB}
        isSubmitted={isSubmitted}
        isCorrect={isCorrect}
        showPetriDish={true}
        petriDishValue={8}
        size={280}
      />

      <div className="flex gap-2">
        {!isSubmitted ? (
          <button
            onClick={handleCheck}
            disabled={selectedA === null || selectedB === null}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: selectedA !== null && selectedB !== null ? '#8b5cf6' : '#475569',
              color: '#ffffff',
              opacity: selectedA !== null && selectedB !== null ? 1 : 0.5,
            }}
          >
            Check Equation
          </button>
        ) : (
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#475569', color: '#ffffff' }}
          >
            Try Again
          </button>
        )}
      </div>

      {isSubmitted && (
        <div
          className="px-4 py-2 rounded-lg text-sm"
          style={{
            background: isCorrect ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            color: isCorrect ? '#10b981' : '#ef4444',
          }}
        >
          {isCorrect ? 'Perfect! y = 8 × 2^(-n) models exponential decay!' : 'Not quite - check the starting value and decay rate'}
        </div>
      )}
    </div>
  );
};

// ============ LESSON CONTENT ============
// Year 8 Mathematics: Algebraic Proofs & Exponential Functions

const createNodes = (): DialogueNode[] => [
  // Introduction
  {
    id: 'intro-1',
    type: 'chapter-break',
    chapterNumber: 1,
    chapterTitle: 'The Ancient Puzzle',
  },
  {
    id: 'alex-1',
    type: 'dialogue',
    characterId: 'alex',
    text: "Dr. Euler, my homework says I need to prove that (a + b)² = a² + 2ab + b². But I don't even know what 'prove' means in maths!",
    emotion: 'confused',
  },
  {
    id: 'euler-1',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Excellent question, Alex! A mathematical proof shows WHY something is true, not just that it works for some numbers. And for this identity, we can prove it visually - no algebra needed at first!",
    emotion: 'excited',
  },
  {
    id: 'alex-2',
    type: 'dialogue',
    characterId: 'alex',
    text: 'Visually? Like with pictures?',
    emotion: 'thinking',
  },
  {
    id: 'euler-2',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Exactly! This is how ancient mathematicians like Euclid proved theorems over 2000 years ago. Let me show you a square with side length (a + b)...",
    emotion: 'happy',
  },

  // Visual Reveal
  {
    id: 'reveal-1',
    type: 'reveal',
    revealTitle: 'The (a + b)² Square',
    revealContent: 'A square with side (a + b) has total area (a + b)². But we can divide it into smaller pieces to see what that actually equals!',
    customComponent: (
      <div className="flex justify-center">
        <AlgebraicProof
          layout="four-rectangles"
          targetArea="(a+b)²"
          showLabels={true}
          size={240}
        />
      </div>
    ),
  },

  {
    id: 'euler-3',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Look at this square! It's divided into four parts. Can you identify what each section represents?",
    emotion: 'thinking',
  },

  // First Interactive Question
  {
    id: 'q1',
    type: 'question-mc',
    questionText: 'The large white square in the top-left corner has sides of length "a". What is its area?',
    options: [
      { id: 'a', text: '2a' },
      { id: 'b', text: 'a²' },
      { id: 'c', text: 'ab' },
      { id: 'd', text: 'a + b' },
    ],
    correctOptionId: 'b',
    explanation: 'A square with side "a" has area a × a = a². This is one piece of our proof!',
    xpReward: 15,
  },

  {
    id: 'alex-3',
    type: 'dialogue',
    characterId: 'alex',
    text: "Oh! So the small square in the bottom-right must be b², because it has sides of length b!",
    emotion: 'excited',
  },

  {
    id: 'euler-4',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Brilliant! Now what about those two blue rectangles?",
    emotion: 'happy',
  },

  // Second Question
  {
    id: 'q2',
    type: 'question-mc',
    questionText: 'Each blue rectangle has dimensions a × b. What is the area of ONE rectangle?',
    options: [
      { id: 'a', text: 'a + b' },
      { id: 'b', text: 'a²b²' },
      { id: 'c', text: 'ab' },
      { id: 'd', text: '2ab' },
    ],
    correctOptionId: 'c',
    explanation: 'Rectangle area = length × width = a × b = ab. And since there are TWO identical rectangles, together they give us 2ab!',
    xpReward: 20,
  },

  // The proof comes together
  {
    id: 'intro-2',
    type: 'chapter-break',
    chapterNumber: 2,
    chapterTitle: 'The Proof Revealed',
  },

  {
    id: 'euler-5',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Now let's put it all together. The big square has area (a + b)². But it's also the SUM of all four pieces!",
    emotion: 'excited',
  },

  {
    id: 'reveal-2',
    type: 'reveal',
    revealTitle: 'The Complete Proof',
    revealContent: '(a + b)² = a² + ab + ab + b² = a² + 2ab + b²',
  },

  // Interactive Selection Challenge
  {
    id: 'q3',
    type: 'question-mc',
    questionText: 'How many pieces represent "ab" in our diagram?',
    options: [
      { id: 'a', text: '1 piece' },
      { id: 'b', text: '2 pieces' },
      { id: 'c', text: '3 pieces' },
      { id: 'd', text: '4 pieces' },
    ],
    correctOptionId: 'b',
    explanation: 'There are exactly 2 rectangles, each with area ab. This is why we get 2ab in the expansion!',
    xpReward: 20,
  },

  {
    id: 'alex-4',
    type: 'dialogue',
    characterId: 'alex',
    text: "This is amazing! I can literally SEE why it's a² + 2ab + b². The picture proves it!",
    emotion: 'happy',
  },

  {
    id: 'euler-6',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "And that's the beauty of geometric proofs! They make abstract algebra tangible. This technique dates back to Euclid's Elements, written around 300 BCE!",
    emotion: 'neutral',
  },

  // Interactive Challenge
  {
    id: 'reveal-3',
    type: 'reveal',
    revealTitle: 'Your Turn: Find 2ab',
    revealContent: 'Select the shapes that together equal 2ab (the two ab rectangles)',
    customComponent: <InteractiveProof correctShapes={['ab-top', 'ab-bottom']} targetLabel="2ab" />,
  },

  // Transition to Exponential Functions
  {
    id: 'intro-3',
    type: 'chapter-break',
    chapterNumber: 3,
    chapterTitle: 'The Growth Mystery',
  },

  {
    id: 'alex-5',
    type: 'dialogue',
    characterId: 'alex',
    text: "Dr. Euler, that was incredible! But I also have another question about my biology homework. There's this plant that loses half its leaves every day...",
    emotion: 'thinking',
  },

  {
    id: 'euler-7',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Ah! That's actually maths too - exponential functions! Let me guess... it starts with some leaves, then halves each day?",
    emotion: 'excited',
  },

  {
    id: 'alex-6',
    type: 'dialogue',
    characterId: 'alex',
    text: 'Yes! It starts with 8 leaves. After one day it has 4, then 2... How do I write this as an equation?',
    emotion: 'confused',
  },

  {
    id: 'euler-8',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "This is exponential decay! When something decreases by the same proportion each time, we use the formula y = a × b^n, where 'a' is the starting value and 'b' relates to the rate of change.",
    emotion: 'neutral',
  },

  {
    id: 'reveal-4',
    type: 'reveal',
    revealTitle: 'Exponential Functions',
    revealContent: 'y = a × b^n where:\n• a = starting value (initial amount)\n• b = growth/decay factor\n• n = number of time periods\n\nFor decay, we often write y = a × b^(-n) where b > 1',
  },

  // Exponential Question
  {
    id: 'q4',
    type: 'question-mc',
    questionText: 'If the plant starts with 8 leaves and loses half each day, what is the starting value "a"?',
    options: [
      { id: 'a', text: '2' },
      { id: 'b', text: '4' },
      { id: 'c', text: '8' },
      { id: 'd', text: '16' },
    ],
    correctOptionId: 'c',
    explanation: 'The starting value is always the initial amount before any time has passed - in this case, 8 leaves at day 0.',
    xpReward: 15,
  },

  {
    id: 'euler-9',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: 'Great! Now for the decay factor. Since the leaves halve (divide by 2), we can write this as multiplying by 2^(-1) = 1/2 each day.',
    emotion: 'thinking',
  },

  {
    id: 'q5',
    type: 'question-mc',
    questionText: 'What is the value of "b" in our equation y = 8 × b^(-n)?',
    options: [
      { id: 'a', text: '1/2' },
      { id: 'b', text: '2' },
      { id: 'c', text: '4' },
      { id: 'd', text: '8' },
    ],
    correctOptionId: 'b',
    explanation: 'When we write it as y = 8 × 2^(-n), b = 2. The negative exponent handles the halving: 2^(-1) = 1/2, 2^(-2) = 1/4, etc.',
    xpReward: 25,
  },

  // Interactive Exponential Builder
  {
    id: 'reveal-5',
    type: 'reveal',
    revealTitle: 'Build the Function',
    revealContent: 'Now try building the exponential function that models the leaf decay!',
    customComponent: <ExponentialChallenge />,
  },

  {
    id: 'alex-7',
    type: 'dialogue',
    characterId: 'alex',
    text: 'So the equation y = 8 × 2^(-n) tells us exactly how many leaves there will be after any number of days?',
    emotion: 'excited',
  },

  {
    id: 'euler-10',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Exactly! Plug in n = 0, you get 8 leaves. Plug in n = 1, you get 4 leaves. Plug in n = 3, and you'd get... can you tell me?",
    emotion: 'happy',
  },

  // Final Calculation
  {
    id: 'q6',
    type: 'question-input',
    questionText: 'How many leaves would there be after 3 days? (Calculate: 8 × 2^(-3))',
    correctAnswer: '1',
    explanation: '8 × 2^(-3) = 8 × 1/8 = 1 leaf! After 3 days of halving: 8 → 4 → 2 → 1',
    xpReward: 30,
  },

  // Conclusion
  {
    id: 'intro-4',
    type: 'chapter-break',
    chapterNumber: 4,
    chapterTitle: 'Mathematics Everywhere',
  },

  {
    id: 'alex-8',
    type: 'dialogue',
    characterId: 'alex',
    text: "Today I learned that squares can prove algebra, and equations can predict nature. Maths really is everywhere!",
    emotion: 'happy',
  },

  {
    id: 'euler-11',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "You've discovered something beautiful today, Alex. Algebraic proofs show us the 'why' behind formulas, and exponential functions model growth and decay in everything from biology to finance to radioactive decay!",
    emotion: 'excited',
  },

  {
    id: 'reveal-6',
    type: 'reveal',
    revealTitle: 'Key Takeaways',
    revealContent: '1. (a + b)² = a² + 2ab + b² - proven visually with squares!\n2. Exponential decay: y = a × b^(-n)\n3. Mathematical proofs show WHY something is true\n4. Visual proofs connect geometry and algebra',
  },

  {
    id: 'euler-12',
    type: 'dialogue',
    characterId: 'dr-euler',
    text: "Keep questioning, keep exploring, and remember - the most beautiful mathematics often comes from the simplest questions!",
    emotion: 'happy',
  },

  // Completion
  {
    id: 'complete',
    type: 'complete',
  },
];

// ============ COMPONENT ============

interface Year8MathsLessonProps {
  onClose?: () => void;
}

const Year8MathsLesson: React.FC<Year8MathsLessonProps> = ({ onClose }) => {
  const nodes = createNodes();

  return (
    <DialogueLesson
      title="Algebraic Proofs & Exponential Functions"
      subject="maths"
      characters={characters}
      nodes={nodes}
      onClose={onClose}
    />
  );
};

export default Year8MathsLesson;
